import { useRef, useState } from 'react';
import { Anchor, Clock3, Leaf, Plane, Ship, TowerControl, Users } from 'lucide-react';
import { MapViewport, type MapMarker } from '../components/MapViewport';
import { mobilityLayer } from '../map/data/demoLayers';
import { MetricCard, Panel, Segmented } from '../components/ui';
import type { WorldViewportHandle } from '../map/WorldViewport';

const flights = [
  ['QTR732', 'DOH → CDG', '36,000 ft'],
  ['DAL456', 'JFK → LAX', '34,000 ft'],
  ['UAE789', 'DXB → LHR', '38,000 ft'],
  ['AAL101', 'MIA → MAD', '31,000 ft'],
  ['SIA302', 'SIN → FRA', '37,000 ft'],
  ['BAW216', 'LHR → HKG', '35,000 ft'],
  ['AFR380', 'CDG → JNB', '33,000 ft'],
  ['ACA888', 'YYZ → NRT', '34,000 ft'],
];

const flightCoordinates = [
  [35.5, 44.1], [-104.8, 39.1], [18.8, 46.2], [-33.4, 35.6],
  [79.3, 33.8], [82.5, 42.0], [20.1, 13.5], [-159.0, 48.0],
  [-61.3, 45.2], [-12.4, 50.0], [50.2, 31.0], [-74.2, 36.2],
  [112.0, 28.4], [101.5, 39.6], [8.2, 28.0], [147.3, 44.0],
  [-90.4, 28.0], [-44.0, 22.0], [61.0, 52.0], [126.1, 37.0],
  [32.0, -4.0], [-23.0, -18.0], [139.2, 31.0], [4.5, 54.0],
  [-130.0, 43.0], [72.0, 14.0],
] as const;

const flightMarkers: MapMarker[] = flightCoordinates.map(([longitude, latitude], index) => ({
  id: `flight-${index}`,
  longitude,
  latitude,
  altitude: 9_500 + (index % 6) * 420,
  tone: (['blue', 'cyan', 'yellow', 'purple'] as const)[index % 4],
  glyph: '✈',
  label: flights[index % flights.length][0],
}));

const shipCoordinates = [
  [103.7, 1.2], [32.5, 29.8], [-79.8, 9.1], [-43.2, -23.1],
  [72.7, 18.7], [121.5, 30.8], [4.0, 51.8], [-46.3, -25.0],
  [55.0, 24.4], [129.2, 34.8], [-5.4, 36.0], [18.2, -34.5],
  [-122.5, 37.6],
] as const;

const shipMarkers: MapMarker[] = shipCoordinates.map(([longitude, latitude], index) => ({
  id: `ship-${index}`,
  longitude,
  latitude,
  tone: 'green' as const,
  glyph: '▰',
  label: `Vessel ${index + 1}`,
}));

export function MobilityPage() {
  const mapController = useRef<WorldViewportHandle | null>(null);
  const [mode, setMode] = useState('Air Traffic');
  const [selected, setSelected] = useState(flights[0][0]);
  const [expanded, setExpanded] = useState(false);
  const isAir = mode === 'Air Traffic';
  const selectObject = (label: string) => {
    setSelected(label);
    const marker = (isAir ? flightMarkers : shipMarkers).find((item) => item.label === label);
    if (!marker) return;
    const { longitude, latitude } = marker;
    void mapController.current?.flyTo([longitude, latitude, 2_500_000], { durationMs: 700 });
  };
  return (
    <div className="mobility-layout">
      <Panel className="mobility-list">
        <Segmented items={['Air Traffic', 'Maritime Traffic']} value={mode} onChange={(next) => { setMode(next); setSelected(next === 'Air Traffic' ? flights[0][0] : 'Vessel 1'); }} />
        <div className="mobility-list__title"><span>{isAir ? 'Live flights' : 'Live vessels'}</span><em><i className="live-dot" /> Live</em></div>
        <div className="flight-list">
          {flights.slice(0, expanded ? flights.length : 5).map(([code, route, altitude], index) => (
            <button type="button" key={code} className={selected === (isAir ? code : `Vessel ${index + 1}`) ? 'is-selected' : ''} onClick={() => selectObject(isAir ? code : `Vessel ${index + 1}`)}>
              {isAir ? <Plane size={19} /> : <Ship size={19} />}
              <span><strong>{isAir ? code : `IMO-${93120 + index}`}</strong><small>{isAir ? route : ['Singapore → Rotterdam','Panama → Houston','Shanghai → Busan','Santos → Lagos'][index % 4]}</small></span>
              <em>{isAir ? altitude : `${14 + index} kn`}</em>
            </button>
          ))}
        </div>
        <button type="button" className="text-button mobility-list__footer" onClick={() => setExpanded((value) => !value)}>{expanded ? 'Show fewer' : `View all ${isAir ? 'flights' : 'vessels'} →`}</button>
      </Panel>
      <MapViewport controllerRef={mapController} markers={isAir ? flightMarkers : shipMarkers} layers={isAir ? [mobilityLayer] : []} initialCamera={{ longitude: 20, latitude: 18, height: 21_000_000, pitch: -90 }} className="mobility-map" onMarkerClick={(marker) => selectObject(marker.label ?? marker.id)}>
        <div className="map-caption">Following <strong>{selected}</strong> · coverage latency 11s</div>
      </MapViewport>
      <Panel className="mobility-kpis" title={<span className="eyebrow">Global mobility overview</span>}>
        <div className="mobility-kpi-grid">
          <MetricCard label="Flights in air" value="9,842" delta="+4.3%" tone="blue" icon={<Plane size={17} />} />
          <MetricCard label="Active maritime vessels" value="2,736" delta="+2.1%" tone="cyan" icon={<Ship size={17} />} />
          <MetricCard label="Delays" value="312" delta="+7.8%" tone="red" icon={<Clock3 size={17} />} />
          <MetricCard label="Airports active" value="1,482" delta="+1.2%" tone="green" icon={<TowerControl size={17} />} />
          <MetricCard label="Countries connected" value="238" delta="+0.9%" tone="green" icon={<Users size={17} />} />
          <MetricCard label="Avg. flight time" value="2h 48m" delta="-3.6%" tone="green" icon={<Clock3 size={17} />} />
          <MetricCard label="Emission today" value="215,600 t" delta="-1.8%" tone="green" icon={<Leaf size={17} />} />
        </div>
      </Panel>
      <Panel className="mobility-legend">
        <div className="legend-row"><span><i className="route-line route-line--blue" />Commercial flights</span><span><i className="route-line route-line--yellow" />Cargo flights</span><span><i className="route-line route-line--purple" />Private flights</span><span><i className="route-line route-line--green" />Maritime vessels</span><span><Anchor size={14} /> Major hubs</span></div>
      </Panel>
    </div>
  );
}
